/* eslint-disable @next/next/no-img-element */
"use client";

import { Node, mergeAttributes } from "@tiptap/core";
import {
    NodeViewContent,
    NodeViewWrapper,
    ReactNodeViewRenderer,
    type ReactNodeViewProps,
} from "@tiptap/react";
import * as React from "react";
import {
    DEFAULT_BORDER_RADIUS,
    getMediaSelectionStyle,
    scaledPx,
} from "../../lib/utils";
import { BlockSettingsButton, RemoveItemButton } from "../blockControls";
import { BlockItemsReorder } from "../blockItemsReorder";
import { BlockTypeSwitcher } from "../blockTypeSwitcher";
import { SCENE_BLOCK_REGISTRY } from "../registry";
import {
    useMetadata,
    useSceneElements,
    useSelectedSceneId,
    useSelection,
    useUiActions,
} from "@/features/lomdaEditorV2/store";
import { useRichTextToolbar } from "../../richTextEditor/richTextToolbarContext";
import { Slider } from "@/components/ui/slider";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import { RotateCcw } from "lucide-react";

const D = SCENE_BLOCK_REGISTRY.imageGrid;

const PLACEHOLDER_IMAGE = "/images/couple.jpg";

const HEIGHT_MIN = 160;
const HEIGHT_MAX = 720;
const HEIGHT_STEP = 20;
const HEIGHT_DEFAULT = 320;

const RADIUS_MIN = 0;
const RADIUS_MAX = 80;
const RADIUS_STEP = 4;
const RADIUS_DEFAULT = DEFAULT_BORDER_RADIUS;

export function getImageGridDefaultItem(language: string) {
    const isHe = language === "he";
    return {
        type: D.itemNodeName,
        attrs: { image: null },
        content: [
            {
                type: D.titleField.nodeName,
                attrs: { textAlign: "start" },
                content: [
                    {
                        type: "text",
                        text: isHe ? "רגע מתוך השיעור" : "A moment from the lesson",
                    },
                ],
            },
            {
                type: D.bodyField.nodeName,
                attrs: { textAlign: "start" },
                content: [
                    {
                        type: "text",
                        text: isHe
                            ? "תיאור קצר שמסביר מה רואים בתמונה ולמה זה רלוונטי לנושא"
                            : "A short caption explaining what's in the image and why it matters here",
                    },
                ],
            },
        ],
    };
}

export function buildDefaultImageGrid(
    count: number = D.itemCount.default,
    language: string = "en"
) {
    const item = getImageGridDefaultItem(language);
    return {
        type: D.nodeName,
        content: Array.from({ length: count }, () => item),
    };
}

interface SettingSliderProps {
    labelKey: string;
    value: number;
    defaultValue: number;
    min: number;
    max: number;
    step: number;
    onChange: (v: number) => void;
}

function SettingSlider({
    labelKey,
    value,
    defaultValue,
    min,
    max,
    step,
    onChange,
}: SettingSliderProps) {
    const { locale } = useLocale();
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                    {t(labelKey, locale)}
                </span>
                <div className="flex items-center gap-1.5">
                    {value !== defaultValue ? (
                        <button
                            type="button"
                            aria-label={t("lomda.scene-block.reset", locale)}
                            className="!cursor-pointer text-muted-foreground hover:text-foreground transition-colors inline-flex items-center"
                            onClick={(e) => {
                                e.preventDefault();
                                onChange(defaultValue);
                            }}
                        >
                            <RotateCcw className="size-3" />
                        </button>
                    ) : null}
                    <span className="text-xs font-medium tabular-nums">
                        {value}px
                    </span>
                </div>
            </div>
            <Slider
                dir="ltr"
                min={min}
                max={max}
                step={step}
                value={[value]}
                onValueChange={([next]) => onChange(next)}
            />
        </div>
    );
}

function ImageGridView({
    node,
    updateAttributes,
    editor,
    getPos,
}: ReactNodeViewProps) {
    const metadata = useMetadata();
    const { setSelectedBlockImage } = useUiActions();
    const language = metadata?.language ?? "en";
    // Columns auto-track item count, so the row always fills its container's
    // full width and each image stretches/shrinks to its share. Height and
    // radius are block-level so every image stays visually consistent.
    const count = node.childCount;
    const imageHeight =
        (node.attrs.imageHeight as number | undefined) ?? HEIGHT_DEFAULT;
    const imageRadius =
        (node.attrs.imageRadius as number | undefined) ?? RADIUS_DEFAULT;

    const insertItem = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        const end = pos + node.nodeSize - 1;
        editor
            .chain()
            .focus()
            .insertContentAt(end, getImageGridDefaultItem(language))
            .run();
    };

    const deleteBlock = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run();
    };

    const wrapperStyle = {
        "--cols": count,
        "--image-height": scaledPx(imageHeight),
        "--image-radius": scaledPx(imageRadius),
    } as React.CSSProperties;

    return (
        <NodeViewWrapper
            as={D.blockTag}
            data-scene-block={D.type}
            className="group/scene-block relative w-full"
            style={wrapperStyle}
        >
            <BlockSettingsButton
                customSection={
                    <div className="flex flex-col gap-3">
                        <BlockTypeSwitcher
                            editor={editor}
                            getPos={getPos}
                            node={node}
                        />
                        <SettingSlider
                            labelKey="lomda.scene-block.image-height"
                            value={imageHeight}
                            defaultValue={HEIGHT_DEFAULT}
                            min={HEIGHT_MIN}
                            max={HEIGHT_MAX}
                            step={HEIGHT_STEP}
                            onChange={(v) =>
                                updateAttributes({ imageHeight: v })
                            }
                        />
                        <SettingSlider
                            labelKey="lomda.scene-block.border-radius"
                            value={imageRadius}
                            defaultValue={RADIUS_DEFAULT}
                            min={RADIUS_MIN}
                            max={RADIUS_MAX}
                            step={RADIUS_STEP}
                            onChange={(v) =>
                                updateAttributes({ imageRadius: v })
                            }
                        />
                        <BlockItemsReorder
                            editor={editor}
                            getPos={getPos}
                            node={node}
                            getItemMeta={(item) => ({
                                label:
                                    item.firstChild?.textContent.trim() ?? "",
                                thumbnail:
                                    (item.attrs.image as string | null) ??
                                    PLACEHOLDER_IMAGE,
                            })}
                            onReorder={() => setSelectedBlockImage(null)}
                        />
                    </div>
                }
                onAddItem={insertItem}
                addItemLabelKey="lomda.scene-block.add-item"
                onDelete={deleteBlock}
            />
            <NodeViewContent as="div" />
        </NodeViewWrapper>
    );
}

function ImageGridItemView({ editor, getPos, node }: ReactNodeViewProps) {
    const selectedSceneId = useSelectedSceneId();
    const elements = useSceneElements(selectedSceneId);
    const selection = useSelection();
    const { setSelectedBlockImage } = useUiActions();
    const { activate } = useRichTextToolbar();
    const image = (node.attrs.image as string | null | undefined) ?? null;
    // The imageGrid lives inside the scene's text element; block-image
    // selection is keyed on that element id.
    const textElementId = elements.find((e) => e.kind === "text")?.id ?? null;

    const myPos = typeof getPos === "function" ? getPos() : null;
    const isSelected =
        selection.kind === "block-image" &&
        textElementId != null &&
        selection.elementId === textElementId &&
        myPos != null &&
        selection.pos === myPos;

    const removeItem = () => {
        if (myPos == null) return;
        editor
            .chain()
            .focus()
            .deleteRange({ from: myPos, to: myPos + node.nodeSize })
            .run();
    };

    const handleImageClick = () => {
        if (!textElementId || myPos == null) return;
        // Activate the scene's editor in the toolbar context so the global
        // BlockImageFormattingBar can read/update node attrs through it.
        activate(editor);
        if (isSelected) {
            setSelectedBlockImage(null);
        } else {
            setSelectedBlockImage({ elementId: textElementId, pos: myPos });
        }
    };

    return (
        <NodeViewWrapper
            as={D.itemTag}
            className="group/scene-block-item relative flex flex-col min-w-0"
        >
            <RemoveItemButton onClick={removeItem} />
            <button
                type="button"
                contentEditable={false}
                data-block-image-target
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleImageClick();
                }}
                className="w-full !cursor-pointer hover:opacity-90 transition-opacity overflow-hidden"
                style={{
                    height: "var(--image-height)",
                    borderRadius: "var(--image-radius)",
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    ...getMediaSelectionStyle(isSelected),
                }}
            >
                <img
                    src={image ?? PLACEHOLDER_IMAGE}
                    alt=""
                    className="w-full h-full object-cover block"
                />
            </button>
            <NodeViewContent as="div" />
        </NodeViewWrapper>
    );
}

export const ImageGrid = Node.create({
    name: D.nodeName,
    group: "block",
    content: `${D.itemNodeName}+`,
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            imageHeight: {
                default: HEIGHT_DEFAULT,
                // Number(null) is 0 (finite!) so without the null-check below,
                // a missing attribute round-trips as height 0.
                parseHTML: (el) => {
                    const raw = el.getAttribute("data-image-height");
                    if (raw == null) return HEIGHT_DEFAULT;
                    const v = Number(raw);
                    return Number.isFinite(v) ? v : HEIGHT_DEFAULT;
                },
                renderHTML: (attrs) => ({
                    "data-image-height": String(
                        attrs.imageHeight ?? HEIGHT_DEFAULT
                    ),
                }),
            },
            imageRadius: {
                default: RADIUS_DEFAULT,
                parseHTML: (el) => {
                    const raw = el.getAttribute("data-image-radius");
                    if (raw == null) return RADIUS_DEFAULT;
                    const v = Number(raw);
                    return Number.isFinite(v) ? v : RADIUS_DEFAULT;
                },
                renderHTML: (attrs) => ({
                    "data-image-radius": String(
                        attrs.imageRadius ?? RADIUS_DEFAULT
                    ),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: `${D.blockTag}[data-scene-block=${D.type}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.blockTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block": D.type,
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(ImageGridView);
    },
});

export const ImageGridItem = Node.create({
    name: D.itemNodeName,
    group: D.itemNodeName,
    content: `${D.titleField.nodeName} ${D.bodyField.nodeName}`,
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            image: {
                default: null,
                parseHTML: (el) => el.getAttribute("data-image") ?? null,
                renderHTML: (attrs) =>
                    attrs.image ? { "data-image": attrs.image } : {},
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: `${D.itemTag}[data-scene-block-item=${D.type}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.itemTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-item": D.type,
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(ImageGridItemView);
    },
});

export const ImageGridTitle = Node.create({
    name: D.titleField.nodeName,
    group: `${D.type}Field`,
    content: "inline*",
    defining: true,

    parseHTML() {
        return [
            {
                tag: `${D.titleField.htmlTag}[data-scene-block-field=${D.titleField.nodeName}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.titleField.htmlTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-field": D.titleField.nodeName,
            }),
            0,
        ];
    },
});

export const ImageGridBody = Node.create({
    name: D.bodyField.nodeName,
    group: `${D.type}Field`,
    content: "inline*",
    defining: true,

    parseHTML() {
        return [
            {
                tag: `${D.bodyField.htmlTag}[data-scene-block-field=${D.bodyField.nodeName}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.bodyField.htmlTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-field": D.bodyField.nodeName,
            }),
            0,
        ];
    },
});

export const imageGridExtensions = [
    ImageGrid,
    ImageGridItem,
    ImageGridTitle,
    ImageGridBody,
];
